import React, { useState, useEffect } from 'react';

// ──────────────────────────────────────────────
// INDI Admin Dashboard
// View and manage landing page submissions
// Access at #/admin
// ──────────────────────────────────────────────

export default function AdminDashboard() {
  const [token, setToken] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState({ type: '', status: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const fetchSubmissions = async (adminToken) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filter.type) params.set('type', filter.type);
      if (filter.status) params.set('status', filter.status);
      params.set('limit', '100');

      const response = await fetch(`${API_URL}/submissions?${params}`, {
        headers: { 'x-admin-token': adminToken || token },
      });
      const data = await response.json();

      if (data.success) {
        setSubmissions(data.submissions);
        setCounts(data.counts);
      } else {
        setError(data.error);
        if (response.status === 401) setAuthenticated(false);
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/submissions?limit=1`, {
        headers: { 'x-admin-token': token },
      });
      if (response.ok) {
        setAuthenticated(true);
        fetchSubmissions(token);
      } else {
        setError('Invalid admin token');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const updateSubmission = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/submissions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-token': token,
        },
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (data.success) {
        setSubmissions(prev =>
          prev.map(s => s._id === id ? data.submission : s)
        );
      }
    } catch (err) {
      console.error('Update error:', err);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      const response = await fetch(`${API_URL}/submissions/${id}`, {
        method: 'DELETE',
        headers: { 'x-admin-token': token },
      });
      const data = await response.json();
      if (data.success) {
        setSubmissions(prev => prev.filter(s => s._id !== id));
        setCounts(prev => ({ ...prev, total: (prev.total || 1) - 1 }));
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const deleteAllSubmissions = async () => {
    const filterLabel = filter.type ? `all "${filter.type}" submissions` : 'ALL submissions';
    if (!window.confirm(`Are you sure you want to delete ${filterLabel}? This cannot be undone.`)) return;
    if (!window.confirm(`Really? This will permanently delete ${filterLabel}.`)) return;
    try {
      const params = new URLSearchParams({ confirm: 'yes' });
      if (filter.type) params.set('type', filter.type);
      const response = await fetch(`${API_URL}/submissions?${params}`, {
        method: 'DELETE',
        headers: { 'x-admin-token': token },
      });
      const data = await response.json();
      if (data.success) {
        fetchSubmissions();
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filter.type) params.set('type', filter.type);
    window.open(`${API_URL}/submissions/export?${params}&x-admin-token=${token}`, '_blank');
  };

  useEffect(() => {
    if (authenticated) fetchSubmissions();
  }, [filter]);

  // ── Styles ──────────────────────────────────
  const colors = {
    bg: '#060A13',
    cardBg: 'rgba(30, 41, 59, 0.5)',
    border: 'rgba(255, 255, 255, 0.06)',
    cyan: '#06B6D4',
    lime: '#84CC16',
    red: '#EF4444',
    orange: '#F59E0B',
    text: '#E8ECF1',
    muted: '#94A3B8',
    dimmed: '#64748B',
  };

  const badgeColors = {
    filmmaker: { bg: 'rgba(132, 204, 22, 0.1)', border: 'rgba(132, 204, 22, 0.3)', color: '#84CC16' },
    investor: { bg: 'rgba(6, 182, 212, 0.1)', border: 'rgba(6, 182, 212, 0.3)', color: '#06B6D4' },
    community: { bg: 'rgba(148, 163, 184, 0.1)', border: 'rgba(148, 163, 184, 0.3)', color: '#94A3B8' },
    waitlist: { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', color: '#F59E0B' },
  };

  const statusColors = {
    new: { bg: 'rgba(6, 182, 212, 0.1)', border: 'rgba(6, 182, 212, 0.3)', color: '#06B6D4' },
    contacted: { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', color: '#F59E0B' },
    in_progress: { bg: 'rgba(132, 204, 22, 0.1)', border: 'rgba(132, 204, 22, 0.3)', color: '#84CC16' },
    closed: { bg: 'rgba(148, 163, 184, 0.1)', border: 'rgba(148, 163, 184, 0.3)', color: '#64748B' },
  };

  // ── Login Screen ──────────────────────────────
  if (!authenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Instrument Sans', sans-serif",
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        `}</style>
        <div style={{
          maxWidth: '400px',
          width: '100%',
          padding: '2.5rem',
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
        }}>
          <div style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '1.5rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #06B6D4 0%, #84CC16 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.25rem',
          }}>
            INDI Admin
          </div>
          <p style={{ color: colors.dimmed, fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            Enter your admin token to access the dashboard.
          </p>

          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Admin token"
              style={{
                width: '100%',
                padding: '0.8rem 1rem',
                background: 'rgba(15, 23, 42, 0.8)',
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                color: colors.text,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.875rem',
                outline: 'none',
                marginBottom: '1rem',
              }}
              required
            />
            {error && (
              <div style={{
                color: colors.red,
                fontSize: '0.8125rem',
                marginBottom: '0.75rem',
              }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.8rem',
                background: colors.cyan,
                color: colors.bg,
                border: 'none',
                borderRadius: '10px',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: '600',
                fontSize: '0.9375rem',
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Connecting...' : 'Access Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ──────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      fontFamily: "'Instrument Sans', sans-serif",
      color: colors.text,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
      `}</style>

      {/* Header */}
      <div style={{
        padding: '1.25rem 2rem',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '1.25rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #06B6D4 0%, #84CC16 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            INDI Admin
          </div>
          <span style={{ color: colors.dimmed, fontSize: '0.8125rem' }}>Submission Dashboard</span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => fetchSubmissions()}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(6, 182, 212, 0.08)',
              border: `1px solid rgba(6, 182, 212, 0.2)`,
              borderRadius: '8px',
              color: colors.cyan,
              fontFamily: "'Instrument Sans', sans-serif",
              fontSize: '0.8125rem',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
          <button
            onClick={handleExport}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(132, 204, 22, 0.08)',
              border: `1px solid rgba(132, 204, 22, 0.2)`,
              borderRadius: '8px',
              color: colors.lime,
              fontFamily: "'Instrument Sans', sans-serif",
              fontSize: '0.8125rem',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Export CSV
          </button>
          <button
            onClick={deleteAllSubmissions}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(239, 68, 68, 0.08)',
              border: `1px solid rgba(239, 68, 68, 0.2)`,
              borderRadius: '8px',
              color: colors.red,
              fontFamily: "'Instrument Sans', sans-serif",
              fontSize: '0.8125rem',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            {filter.type ? `Delete All ${filter.type}` : 'Delete All'}
          </button>
          <button
            onClick={() => { setAuthenticated(false); setToken(''); }}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.dimmed,
              fontFamily: "'Instrument Sans', sans-serif",
              fontSize: '0.8125rem',
              cursor: 'pointer',
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {[
            { label: 'Total', value: counts.total || 0, color: colors.text },
            { label: 'Filmmakers', value: counts.filmmaker || 0, color: '#84CC16' },
            { label: 'Investors', value: counts.investor || 0, color: '#06B6D4' },
            { label: 'Community', value: counts.community || 0, color: '#94A3B8' },
            { label: 'New', value: counts.new || 0, color: '#F59E0B' },
          ].map((stat) => (
            <div
              key={stat.label}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                padding: '1.25rem',
                textAlign: 'center',
              }}
            >
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '1.75rem',
                fontWeight: '500',
                color: stat.color,
              }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '0.8125rem', color: colors.dimmed, marginTop: '0.25rem' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: colors.dimmed, fontWeight: '600' }}>Filter:</span>
          {['', 'filmmaker', 'investor', 'community', 'waitlist'].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(prev => ({ ...prev, type: t }))}
              style={{
                padding: '0.4rem 0.85rem',
                background: filter.type === t ? 'rgba(6, 182, 212, 0.15)' : 'transparent',
                border: `1px solid ${filter.type === t ? 'rgba(6, 182, 212, 0.3)' : colors.border}`,
                borderRadius: '8px',
                color: filter.type === t ? colors.cyan : colors.dimmed,
                fontFamily: "'Instrument Sans', sans-serif",
                fontSize: '0.8125rem',
                cursor: 'pointer',
              }}
            >
              {t || 'All'}
            </button>
          ))}
          <div style={{ width: '1px', height: '20px', background: colors.border, margin: '0 0.25rem' }} />
          {['', 'new', 'contacted', 'in_progress', 'closed'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(prev => ({ ...prev, status: s }))}
              style={{
                padding: '0.4rem 0.85rem',
                background: filter.status === s ? 'rgba(6, 182, 212, 0.15)' : 'transparent',
                border: `1px solid ${filter.status === s ? 'rgba(6, 182, 212, 0.3)' : colors.border}`,
                borderRadius: '8px',
                color: filter.status === s ? colors.cyan : colors.dimmed,
                fontFamily: "'Instrument Sans', sans-serif",
                fontSize: '0.8125rem',
                cursor: 'pointer',
              }}
            >
              {s ? s.replace('_', ' ') : 'All statuses'}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: colors.dimmed }}>
            Loading submissions...
          </div>
        )}

        {/* Submissions List */}
        {!loading && submissions.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: colors.dimmed,
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
          }}>
            No submissions yet. They'll appear here as people fill out forms on the landing page.
          </div>
        )}

        {!loading && submissions.map((sub) => {
          const isExpanded = expandedId === sub._id;
          const badge = badgeColors[sub.type] || badgeColors.community;
          const statusBadge = statusColors[sub.status] || statusColors.new;

          return (
            <div
              key={sub._id}
              style={{
                background: colors.cardBg,
                border: `1px solid ${isExpanded ? 'rgba(6, 182, 212, 0.15)' : colors.border}`,
                borderRadius: '12px',
                marginBottom: '0.75rem',
                overflow: 'hidden',
                transition: 'border-color 200ms ease',
              }}
            >
              {/* Row Header */}
              <div
                onClick={() => setExpandedId(isExpanded ? null : sub._id)}
                style={{
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  cursor: 'pointer',
                }}
              >
                {/* Type Badge */}
                <span style={{
                  padding: '0.2rem 0.6rem',
                  background: badge.bg,
                  border: `1px solid ${badge.border}`,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: badge.color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  flexShrink: 0,
                  width: '85px',
                  textAlign: 'center',
                }}>
                  {sub.type}
                </span>

                {/* Name & Email */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: '600', color: colors.text, marginRight: '0.75rem' }}>
                    {sub.name || '—'}
                  </span>
                  <span style={{ color: colors.dimmed, fontSize: '0.875rem' }}>
                    {sub.email}
                  </span>
                </div>

                {/* Status */}
                <span style={{
                  padding: '0.2rem 0.6rem',
                  background: statusBadge.bg,
                  border: `1px solid ${statusBadge.border}`,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  color: statusBadge.color,
                  flexShrink: 0,
                }}>
                  {sub.status.replace('_', ' ')}
                </span>

                {/* Date */}
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.75rem',
                  color: colors.dimmed,
                  flexShrink: 0,
                }}>
                  {new Date(sub.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>

                {/* Expand indicator */}
                <span style={{ color: colors.dimmed, fontSize: '0.75rem', flexShrink: 0 }}>
                  {isExpanded ? '▲' : '▼'}
                </span>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div style={{
                  padding: '0 1.25rem 1.25rem',
                  borderTop: `1px solid ${colors.border}`,
                  paddingTop: '1rem',
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    {/* Left column - Details */}
                    <div>
                      {sub.type === 'investor' && (
                        <>
                          <DetailRow label="Investment Range" value={sub.investmentRange} />
                          <DetailRow label="Film Investment Experience" value={sub.filmInvestmentExperience?.replace(/-/g, ' ')} />
                          <DetailRow label="Accredited" value={sub.accredited} />
                        </>
                      )}

                      {sub.type === 'filmmaker' && (
                        <>
                          <DetailRow label="Portfolio" value={sub.portfolio} isLink />
                          {sub.projects?.map((p, i) => (
                            <div key={i} style={{
                              padding: '0.75rem',
                              background: 'rgba(6, 182, 212, 0.03)',
                              border: `1px solid ${colors.border}`,
                              borderRadius: '8px',
                              marginBottom: '0.5rem',
                            }}>
                              <div style={{ fontSize: '0.75rem', color: colors.lime, fontFamily: "'JetBrains Mono', monospace", marginBottom: '0.35rem' }}>
                                {i === 0 ? 'Primary Project' : `Additional Project ${i}`}
                              </div>
                              <DetailRow label="Title" value={p.projectTitle} />
                              <DetailRow label="Genre" value={p.genre} />
                              <DetailRow label="Budget" value={p.budget} />
                              {p.synopsis && (
                                <div style={{ fontSize: '0.875rem', color: colors.muted, lineHeight: '1.55', marginTop: '0.35rem' }}>
                                  {p.synopsis}
                                </div>
                              )}
                            </div>
                          ))}
                        </>
                      )}

                      {sub.type === 'community' && (
                        <div style={{ color: colors.dimmed, fontSize: '0.875rem' }}>
                          Community waitlist signup — email only.
                        </div>
                      )}
                    </div>

                    {/* Right column - Actions */}
                    <div>
                      {/* Status Update */}
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.8125rem', color: colors.dimmed, marginBottom: '0.35rem', fontWeight: '600' }}>
                          Status
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {['new', 'contacted', 'in_progress', 'closed'].map((s) => (
                            <button
                              key={s}
                              onClick={() => updateSubmission(sub._id, { status: s })}
                              style={{
                                padding: '0.3rem 0.7rem',
                                background: sub.status === s ? statusColors[s].bg : 'transparent',
                                border: `1px solid ${sub.status === s ? statusColors[s].border : colors.border}`,
                                borderRadius: '6px',
                                color: sub.status === s ? statusColors[s].color : colors.dimmed,
                                fontSize: '0.75rem',
                                fontFamily: "'Instrument Sans', sans-serif",
                                cursor: 'pointer',
                              }}
                            >
                              {s.replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Notes */}
                      <div>
                        <div style={{ fontSize: '0.8125rem', color: colors.dimmed, marginBottom: '0.35rem', fontWeight: '600' }}>
                          Notes
                        </div>
                        <textarea
                          value={editingNotes[sub._id] !== undefined ? editingNotes[sub._id] : (sub.notes || '')}
                          onChange={(e) => setEditingNotes(prev => ({ ...prev, [sub._id]: e.target.value }))}
                          onBlur={() => {
                            if (editingNotes[sub._id] !== undefined && editingNotes[sub._id] !== (sub.notes || '')) {
                              updateSubmission(sub._id, { notes: editingNotes[sub._id] });
                            }
                          }}
                          placeholder="Add notes about this submission..."
                          style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '0.6rem 0.8rem',
                            background: 'rgba(15, 23, 42, 0.8)',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '8px',
                            color: colors.text,
                            fontFamily: "'Instrument Sans', sans-serif",
                            fontSize: '0.875rem',
                            lineHeight: '1.5',
                            resize: 'vertical',
                            outline: 'none',
                          }}
                        />
                      </div>

                      {/* Metadata */}
                      <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: colors.dimmed }}>
                        <div>Submitted: {new Date(sub.submittedAt).toLocaleString()}</div>
                        {sub.ipAddress && <div>IP: {sub.ipAddress}</div>}
                      </div>

                      {/* Delete */}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteSubmission(sub._id); }}
                        style={{
                          marginTop: '1rem',
                          padding: '0.4rem 0.8rem',
                          background: 'rgba(239, 68, 68, 0.08)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          borderRadius: '6px',
                          color: colors.red,
                          fontSize: '0.75rem',
                          fontFamily: "'Instrument Sans', sans-serif",
                          cursor: 'pointer',
                        }}
                      >
                        Delete Submission
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper component
function DetailRow({ label, value, isLink }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: '0.35rem' }}>
      <span style={{ fontSize: '0.8125rem', color: '#64748B', marginRight: '0.5rem' }}>{label}:</span>
      {isLink ? (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', color: '#06B6D4' }}>
          {value}
        </a>
      ) : (
        <span style={{ fontSize: '0.875rem', color: '#B0B8C8' }}>{value}</span>
      )}
    </div>
  );
}
